import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Languages, LogOut, Download, Link2, RefreshCw, FileText, Plus, Trash2, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUSES = ["New", "Under Review", "Quote Sent", "Approved", "Payment Pending", "Paid",
  "Translation In Progress", "Quality Review", "Completed", "Delivered"];

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  "Under Review": "bg-amber-50 text-amber-700 border-amber-200",
  "Quote Sent": "bg-violet-50 text-violet-700 border-violet-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Payment Pending": "bg-orange-50 text-orange-700 border-orange-200",
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Translation In Progress": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Quality Review": "bg-cyan-50 text-cyan-700 border-cyan-200",
  Completed: "bg-slate-100 text-slate-700 border-slate-300",
  Delivered: "bg-slate-800 text-white border-slate-800",
};

const inputCls =
  "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#C87A4B]/40 focus:border-[#C87A4B]";

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-slate-200 p-6">
      <p className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">{label}</p>
      <p className="font-heading text-3xl font-bold text-[#0B111E]">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [logos, setLogos] = useState([]);
  const [newT, setNewT] = useState({ customer_name: "", company: "", position: "", quote: "" });
  const [logoName, setLogoName] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, q, t, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/quotes"),
        api.get("/admin/testimonials"),
        api.get("/admin/logos"),
      ]);
      setStats(s.data); setQuotes(q.data); setTestimonials(t.data); setLogos(l.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(formatApiError(err));
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("lx_token")) return navigate("/admin/login");
    load();
  }, [load, navigate]);

  const updateQuote = async (ref, payload) => {
    try {
      await api.patch(`/admin/quotes/${ref}`, payload);
      toast.success("Enquiry updated");
      await load();
      const fresh = await api.get("/admin/quotes");
      const q = fresh.data.find((x) => x.quote_ref === ref);
      if (q) setSelected(q);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const paymentLink = async (ref) => {
    try {
      const { data } = await api.post(`/admin/quotes/${ref}/payment-link`);
      toast.success("Payment link created and emailed to customer");
      window.open(data.payment_link, "_blank");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/admin/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = statusFilter === "all" ? quotes : quotes.filter((q) => q.status === statusFilter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#0B111E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-[#C87A4B] flex items-center justify-center">
              <Languages className="w-4 h-4" />
            </span>
            <span className="font-heading font-bold">TranslateIt <span className="text-slate-400 font-medium text-sm">Admin</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="w-9 h-9 rounded-lg border border-slate-700 flex items-center justify-center hover:border-[#C87A4B] transition-colors" aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => { localStorage.removeItem("lx_token"); navigate("/admin/login"); }}
              data-testid="admin-logout-btn"
              className="h-9 px-4 rounded-lg border border-slate-700 text-sm flex items-center gap-2 hover:border-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Stat label="Total Enquiries" value={stats?.total ?? "—"} testId="stat-total" />
          <Stat label="New" value={stats?.new ?? "—"} testId="stat-new" />
          <Stat label="Active Projects" value={stats?.active ?? "—"} testId="stat-active" />
          <Stat label="Completed" value={stats?.completed ?? "—"} testId="stat-completed" />
          <Stat label="Est. Value" value={stats ? `£${stats.estimated_value.toLocaleString()}` : "—"} testId="stat-value" />
        </div>

        <Tabs defaultValue="enquiries">
          <TabsList className="bg-white border border-slate-200 mb-6">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="logos" data-testid="tab-logos">Client Logos</TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries">
            <div className="flex items-center gap-3 mb-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="w-52 h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-500">{filtered.length} enquir{filtered.length === 1 ? "y" : "ies"}</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3.5">Reference</th><th className="px-5 py-3.5">Customer</th>
                    <th className="px-5 py-3.5">Languages</th><th className="px-5 py-3.5">Words</th>
                    <th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.quote_ref}
                      data-testid={`enquiry-row-${q.quote_ref}`}
                      onClick={() => { setSelected(q); setPriceInput(q.quoted_price ?? q.price_estimate ?? ""); setNoteInput(q.internal_note || ""); }}
                      className="border-b border-slate-50 hover:bg-[#FAF8F5] cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-3.5 font-mono text-xs font-semibold text-[#C87A4B]">{q.quote_ref}</td>
                      <td className="px-5 py-3.5"><p className="font-semibold text-[#0B111E]">{q.name}</p><p className="text-xs text-slate-400">{q.company || q.email}</p></td>
                      <td className="px-5 py-3.5 text-slate-600">{q.source_language} → {q.target_languages.join(", ")}</td>
                      <td className="px-5 py-3.5 text-slate-600">{q.word_count?.toLocaleString() || "—"}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 ${STATUS_COLORS[q.status] || ""}`}>{q.status}</span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="testimonials">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
              <p className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-4">Add testimonial</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <input data-testid="testimonial-name-input" placeholder="Customer name" value={newT.customer_name} onChange={(e) => setNewT({ ...newT, customer_name: e.target.value })} className={inputCls} />
                <input data-testid="testimonial-company-input" placeholder="Company" value={newT.company} onChange={(e) => setNewT({ ...newT, company: e.target.value })} className={inputCls} />
                <input data-testid="testimonial-position-input" placeholder="Position" value={newT.position} onChange={(e) => setNewT({ ...newT, position: e.target.value })} className={inputCls} />
              </div>
              <textarea data-testid="testimonial-quote-input" placeholder="Quote" value={newT.quote} onChange={(e) => setNewT({ ...newT, quote: e.target.value })} className={`${inputCls} h-20 py-2 mb-3`} />
              <button
                data-testid="testimonial-add-btn"
                onClick={async () => {
                  if (!newT.customer_name || !newT.quote) return toast.error("Name and quote are required");
                  try {
                    await api.post("/admin/testimonials", { ...newT, published: true });
                    setNewT({ customer_name: "", company: "", position: "", quote: "" });
                    toast.success("Testimonial published"); load();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className="h-10 px-5 rounded-lg bg-[#C87A4B] hover:bg-[#B3673A] text-white text-sm font-semibold flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Publish testimonial
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-white rounded-2xl border border-slate-200 p-6">
                  <p className="text-sm text-slate-700 mb-4 leading-relaxed">“{t.quote}”</p>
                  <p className="text-sm font-semibold">{t.customer_name} <span className="font-normal text-slate-400">· {t.company}</span></p>
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      data-testid={`testimonial-toggle-${t.id}`}
                      onClick={async () => { await api.patch(`/admin/testimonials/${t.id}`, { ...t, published: !t.published }); load(); }}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${t.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}
                    >
                      {t.published ? "Published" : "Unpublished"}
                    </button>
                    <button data-testid={`testimonial-delete-${t.id}`} onClick={async () => { await api.delete(`/admin/testimonials/${t.id}`); load(); }} className="text-xs text-red-500 flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="logos">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
              <p className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-4">Upload approved client logo</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="logo-name-input" placeholder="Client name" value={logoName} onChange={(e) => setLogoName(e.target.value)} className={`${inputCls} sm:w-64`} />
                <input data-testid="logo-file-input" type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="text-sm" />
                <button
                  data-testid="logo-upload-btn"
                  onClick={async () => {
                    if (!logoName || !logoFile) return toast.error("Name and image required");
                    const fd = new FormData(); fd.append("name", logoName); fd.append("file", logoFile);
                    try {
                      await api.post("/admin/logos", fd);
                      setLogoName(""); setLogoFile(null); toast.success("Logo published"); load();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="h-10 px-5 rounded-lg bg-[#C87A4B] hover:bg-[#B3673A] text-white text-sm font-semibold flex items-center gap-2"
                >
                  <ImagePlus className="w-4 h-4" /> Upload
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {logos.map((l) => (
                <div key={l.id} data-testid={`admin-logo-${l.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                  <img src={`${API}/logos/${l.id}/image`} alt={l.name} className="h-10 mx-auto object-contain mb-3" />
                  <p className="text-sm font-semibold mb-3">{l.name}</p>
                  <button
                    data-testid={`logo-toggle-${l.id}`}
                    onClick={async () => { await api.patch(`/admin/logos/${l.id}`); load(); }}
                    className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${l.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}
                  >
                    {l.published ? "Published" : "Hidden"}
                  </button>
                </div>
              ))}
              {logos.length === 0 && <p className="text-sm text-slate-400 col-span-4 py-8 text-center">No client logos uploaded yet.</p>}
            </div>
          </TabsContent>

        </Tabs>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="enquiry-detail-modal" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono text-[#C87A4B]">{selected.quote_ref}</span>
                  <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 ${STATUS_COLORS[selected.status]}`}>{selected.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm mt-2">
                <div><p className="text-xs text-slate-400">Customer</p><p className="font-semibold">{selected.name}</p></div>
                <div><p className="text-xs text-slate-400">Company</p><p className="font-semibold">{selected.company || "—"}</p></div>
                <div><p className="text-xs text-slate-400">Email</p><p className="font-semibold">{selected.email}</p></div>
                <div><p className="text-xs text-slate-400">Phone</p><p className="font-semibold">{selected.phone || "—"}</p></div>
                <div><p className="text-xs text-slate-400">Languages</p><p className="font-semibold">{selected.source_language} → {selected.target_languages.join(", ")}</p></div>
                <div><p className="text-xs text-slate-400">Word count</p><p className="font-semibold">{selected.word_count?.toLocaleString() || "—"}</p></div>
                <div><p className="text-xs text-slate-400">Industry / Service</p><p className="font-semibold">{selected.industry} · {selected.service_type}</p></div>
                <div><p className="text-xs text-slate-400">Urgency</p><p className="font-semibold">{selected.delivery}</p></div>
                <div><p className="text-xs text-slate-400">Estimate</p><p className="font-semibold">{selected.price_estimate ? `£${selected.price_estimate.toFixed(2)}` : "Manual"}</p></div>
                <div><p className="text-xs text-slate-400">Submitted</p><p className="font-semibold">{new Date(selected.created_at).toLocaleString()}</p></div>
              </div>
              {selected.notes && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-4">{selected.notes}</p>}
              <div className="mt-4">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">Documents ({selected.file_details?.length || 0})</p>
                {selected.file_details?.map((f) => (
                  <button key={f.id} data-testid={`file-download-${f.id}`} onClick={() => downloadFile(f)}
                    className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 mb-2 hover:border-[#C87A4B] transition-colors text-left">
                    <FileText className="w-4 h-4 text-[#C87A4B]" />
                    <span className="text-xs flex-1 truncate">{f.original_filename}</span>
                    <Download className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                ))}
                {!selected.file_details?.length && <p className="text-xs text-slate-400">No documents uploaded.</p>}
              </div>
              <div className="border-t border-slate-100 mt-5 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-400 mb-1.5">Status</p>
                  <Select value={selected.status} onValueChange={(v) => updateQuote(selected.quote_ref, { status: v })}>
                    <SelectTrigger data-testid="enquiry-status-select" className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1.5">Quoted price (£)</p>
                  <div className="flex gap-2">
                    <input data-testid="enquiry-price-input" type="number" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} className={inputCls} />
                    <button data-testid="enquiry-save-price-btn" onClick={() => updateQuote(selected.quote_ref, { quoted_price: parseFloat(priceInput) })}
                      className="h-10 px-4 rounded-lg bg-[#0B111E] text-white text-xs font-semibold shrink-0">Save</button>
                  </div>
                </div>
              </div>
              <textarea data-testid="enquiry-note-input" placeholder="Internal note…" value={noteInput} onChange={(e) => setNoteInput(e.target.value)}
                onBlur={() => noteInput !== (selected.internal_note || "") && updateQuote(selected.quote_ref, { internal_note: noteInput })}
                className={`${inputCls} h-16 py-2 mt-3`} />
              <button
                data-testid="enquiry-payment-link-btn"
                onClick={() => paymentLink(selected.quote_ref)}
                className="mt-4 w-full h-11 rounded-lg bg-[#C87A4B] hover:bg-[#B3673A] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Link2 className="w-4 h-4" /> Create Shopify Payment Link &amp; Email Customer
              </button>
              {selected.payment_link && (
                <p className="text-xs text-slate-500 mt-2 break-all">Active link: {selected.payment_link}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
